/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate } from 'react-router-dom';
import { type ReactNode, type FC, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';
import classNames from 'classnames';
import { useSpaceStore } from '@coze-foundation/space-store';
import { localStorageService } from '@coze-foundation/local-storage';
import { EVENT_NAMES, sendTeaEvent } from '@coze-arch/bot-tea';
import { getExternalLink } from '@rokid/const';

export interface IWorkspaceListItem {
  icon?: ReactNode;
  activeIcon?: ReactNode;
  title?: () => string;
  path?: string;
  dataTestId?: string;
  module?: string;
}

interface IWorkspaceListItemProps extends IWorkspaceListItem {
  currentSubMenu?: string;
}

export const WorkspaceListItem: FC<IWorkspaceListItemProps> = ({
  icon,
  activeIcon,
  title,
  path,
  currentSubMenu,
  dataTestId,
  module,
}) => {
  const navigate = useNavigate();
  const { spaceId, fetchSpaces, spaceList, setSpace } = useSpaceStore(
    useShallow(store => ({
      spaceId: store.space.id,
      fetchSpaces: store.fetchSpaces,
      spaceList: store.spaceList,
      setSpace: store.setSpace,
    }))
  );
  //rokid 二级菜单提级以后，通过这种方式强制查询spaces列表
  useEffect(() => {
    if (spaceList.length) {
      setSpace(spaceList[0].id);
    } else {
      if (!spaceId) {
        fetchSpaces(true);
      }
    }
  }, [spaceId, spaceList]);
  return (
    <div
      onClick={() => {
        if (module === 'workspace') {
          sendTeaEvent(EVENT_NAMES.coze_space_sidenavi_ck, {
            item: title?.() || 'unknown-workspace-submenu',
            navi_type: 'second',
            need_login: true,
            have_access: true,
          });
          localStorageService.setValue('workspace-subMenu', path);
          // Special handling for home menu item
          if (path === 'HOME') {
            navigate('/space/home');
          } else {
            if (!spaceId) {
              // 无spaceId时，workspace模块跳转到帮助链接
              window.location.href = getExternalLink('NO_WORKSPACE_HELP');
            } else {
              navigate(`/space/${spaceId}/${path}`);
            }
          }
        } else {
          if (!spaceId) {
            // 无spaceId时，explore模块也跳转到帮助链接
            window.location.href = getExternalLink('NO_WORKSPACE_HELP');
          } else {
            navigate(path || '/');
          }
        }
      }}
      className={classNames(
        'flex items-center gap-[8px]',
        'transition-colors',
        'rounded-[8px]',
        'h-[32px] w-full',
        'px-[8px]',
        'cursor-pointer',
        'group',
        'hover:coz-mg-secondary-hovered',
        {
          'coz-mg-primary': path === currentSubMenu,
          'coz-fg-plus': path === currentSubMenu,
          'coz-fg-primary': path !== currentSubMenu,
        }
      )}
      id={`workspace-submenu-${path}`}
      data-testid={dataTestId}
    >
      <div className="text-[14px]">
        <div className="w-[16px] h-[16px]">
          {path === currentSubMenu ? activeIcon : icon}
        </div>
      </div>
      <div
        className={classNames(
          'flex-1',
          'text-[14px]',
          'leading-[20px]',
          'font-[500]'
        )}
      >
        {title?.()}
      </div>
    </div>
  );
};
